import React, { useCallback, useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Heading from '../../components/Heading';
import EmptyState from './components/EmptyState';
import { Contact } from 'models/Contact';
import api from 'api';
import useQueryParams from 'hooks/useQueryParams';
import { Loader } from 'ncoded-component-library';
import ResolvePagination from 'components/ResolvePagination';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import ManageContactDrawer from '../../components/ManageContactDrawer';
import { FIRST_PAGE } from 'data/global';
import { ContactsTable } from './components/ContactsTable';
import ContactsCards from './components/ContactsCards';
import useMatchMedia from 'hooks/useMatchMedia';
import confirm from 'modules/confirm';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { SUCCESS_MESSAGES } from 'request-responses';
import ContactsProvider from '../../providers/Contacts';
import ContactsActionMenu from './components/ContactsActionMenu';
import useRefetch from 'hooks/useRefetch';
import HelmetWrapper from 'components/HelmetWrapper';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';

import './ContactsList.styles.scss';
import './ContactsList.styles.responsive.scss';

type ContactsListProps = {
  className?: string;
};

const ContactsList: React.FC<ContactsListProps> = (props) => {
  const { className } = props;

  const [isFetching, setIsFetching] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [totalPages, setTotalPages] = useState(0);

  const { limit, updateLimit } = useNumberOfResultsPerPage();

  const { refetchFlag, refetch } = useRefetch();

  const [isDeleting, setIsDeleting] = useState(false);

  const [selectedContact, setSelectedContact] = useState<Contact>();

  const isPhablet = useMatchMedia('isPhablet');

  const [manageContactDrawer, manageContactDrawerRef] =
    useCallbackRef<SideDrawerRef>();

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const classes = classNames('contacts-list', className);

  const {
    params: { page, sort },
    setQueryParam,
  } = useQueryParams();

  const { t } = useTranslation();

  const updateContacts = useCallback(
    (contact: Contact) => {
      if (contacts.some((c) => c._id === contact._id)) {
        setContacts((prev) =>
          prev.map((c) => (c._id === contact._id ? contact : c)),
        );
      } else {
        refetch();
      }
    },
    [contacts, refetch],
  );

  const handleEditContact = useCallback(
    (contact: Contact) => {
      setSelectedContact(contact);
      manageContactDrawer.setOpen(true);
    },
    [manageContactDrawer],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (
        await confirm(
          {
            title: t('areYouSureDeleteContact'),
            content: t('deleteWarning'),
            type: 'delete',
          },
          true,
        )
      ) {
        try {
          setIsDeleting(true);
          await api.contacts.deleteContact(id);
          popSuccess(t(SUCCESS_MESSAGES.DELETE_CONTACT));
          refetch();
        } catch (e) {
          popServerError(e);
        } finally {
          setIsDeleting(false);
        }
      }
    },
    [popServerError, popSuccess, t, refetch],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsFetching(true);
        const {
          data: { items, totalPages },
        } = await api.contacts.getContacts({
          $page: page,
          $limit: limit,
          $sort: sort,
        });

        setContacts(items);
        setTotalPages(totalPages);

        if (+page > 1 && +page > totalPages) {
          setQueryParam('page', FIRST_PAGE);
        }
      } catch (e) {
        return;
      } finally {
        setIsFetching(false);
      }
    })();
  }, [page, refetchFlag, sort, limit, setQueryParam]);

  return (
    <ContactsProvider contacts={contacts} refetch={refetch}>
      <HelmetWrapper title={t('contacts')} />

      <div className={classes}>
        <Heading title={t('contacts')} onSuccess={updateContacts}>
          <NumberOfResultsPerPageFilter
            limit={limit}
            updateLimit={updateLimit}
          />
        </Heading>

        {isFetching && isPhablet && <Loader />}

        {!isFetching && !contacts.length ? (
          <div className="white-panel">
            <EmptyState updateContacts={refetch} />
          </div>
        ) : (
          <>
            {isPhablet ? (
              <ContactsCards
                contacts={contacts}
                handleDelete={handleDelete}
                handleEdit={handleEditContact}
              />
            ) : (
              <ContactsTable
                contacts={contacts}
                isLoading={isFetching}
                isDeleting={isDeleting}
                handleEdit={handleEditContact}
              />
            )}

            <ContactsActionMenu />

            <ResolvePagination totalPages={totalPages} />

            <ManageContactDrawer
              contact={selectedContact}
              ref={manageContactDrawerRef}
              onSuccess={updateContacts}
              onDelete={handleDelete}
            />
          </>
        )}
      </div>
    </ContactsProvider>
  );
};

export default ContactsList;
